import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import reviewCommentsService from '~/services/reviewComments/reviewComments.service';
import { useTranslation } from 'react-i18next';
import { addToast } from "@heroui/react"

export const useReviewComments = (reviewId: string, limit = 10) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const commentsQuery = useInfiniteQuery({
    queryKey: ['review-comments', reviewId],
    queryFn: ({ pageParam = 0 }) => 
      reviewCommentsService.getCommentsByReviewId(reviewId, limit, pageParam as number),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      
      const loadedCommentsCount = allPages.length * limit;
      return lastPage.count > loadedCommentsCount ? loadedCommentsCount : undefined;
    },
    initialPageParam: 0,
    enabled: !!reviewId, // Only run the query if a reviewId is provided
  });

  // Flatten the comments from all pages
  const comments = commentsQuery.data?.pages
    .filter(page => page !== null)
    .flatMap(page => page.comments) || [];
  const commentsCount = commentsQuery.data?.pages[0]?.count || 0;

  const createCommentMutation = useMutation({
    mutationFn: ({ text }: { text: string }) => 
      reviewCommentsService.createComment(reviewId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-comments', reviewId] });
      // Invalidate review queries to update comment count displayed in reviews
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review', reviewId] });
      addToast({
        title: t('profile.success'),
        color: "success",
        timeout: 3000,
      });
    },
    onError: () => {
      addToast({
        title: t('comments.commentError'),
        color: "danger",
        timeout: 3000,
      });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => reviewCommentsService.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-comments', reviewId] });
      // Invalidate review queries to update comment count displayed in reviews
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review', reviewId] });
      addToast({
        title: t('comments.commentDeleted'),
        color: "success",
        timeout: 3000,
      });
    },
    onError: () => {
      addToast({
        title: t('comments.deleteError'),
        color: "danger",
        timeout: 3000,
      });
    },
  });

  return {
    comments,
    commentsCount,
    isLoading: commentsQuery.isLoading,
    isFetchingNextPage: commentsQuery.isFetchingNextPage,
    hasNextPage: commentsQuery.hasNextPage,
    fetchNextPage: commentsQuery.fetchNextPage,
    error: commentsQuery.error,
    createComment: createCommentMutation.mutate,
    isCreatingComment: createCommentMutation.isPending,
    deleteComment: deleteCommentMutation.mutate,
    isDeletingComment: deleteCommentMutation.isPending,
  };
}; 